/**
 * Styles for OCEAN collections: the save picker, Profile → Saved → OCEAN
 * (collection tiles, a collection's cards, its menu) and a shared collection
 * opened from a link. Kept apart from styles.tsx (the page's own rules) and
 * injected once, the same way; every rule is scoped `.ocx .occ-…` and uses the
 * tokens styles.tsx defines on `.ocx`.
 */
import { useLayoutEffect } from 'react';

const CSS = `
/* ---------- collections: Profile → Saved → OCEAN ---------- */
.ocx .occ-head { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.ocx .occ-h { font-family:var(--font-display); font-size:16px; font-weight:700; color:var(--ink); margin:0; }
.ocx .occ-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:12px; margin-top:14px; }
.ocx .occ-tile { position:relative; border:1px solid var(--border); border-radius:12px; background:var(--gray-0); display:flex; align-items:stretch; transition:border-color .12s ease, box-shadow .12s ease; }
.ocx .occ-tile:hover { border-color:var(--primary-300); box-shadow:var(--shadow-card); }
.ocx .occ-tile-open { flex:1; min-width:0; display:flex; align-items:center; gap:12px; padding:14px 6px 14px 14px; text-align:left; }
.ocx .occ-tile-icon { flex:none; width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; background:var(--primary-100); color:var(--primary-700); }
.ocx .occ-tile-name { font-family:var(--font-display); font-size:15px; font-weight:700; color:var(--ink); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ocx .occ-sub { font-size:12px; color:var(--gray-300); margin-top:3px; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.ocx .occ-flag { font-size:11px; font-weight:700; padding:2px 8px; border-radius:var(--radius-pill); background:var(--success-bg); color:var(--success-400); }
.ocx .occ-flag.muted { background:var(--gray-50); color:var(--gray-300); }
.ocx .occ-more { flex:none; align-self:flex-start; margin:8px 6px 0 0; }
.ocx .occ-new { display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin-top:12px; }
.ocx .occ-new .oc-input { flex:1 1 200px; padding:10px 13px; font-size:14px; }
.ocx .occ-back { display:inline-flex; align-items:center; gap:4px; font-size:13px; font-weight:700; color:var(--primary-700); }
.ocx .occ-back:hover { text-decoration:underline; text-underline-offset:3px; }
.ocx .occ-detail-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-wrap:wrap; margin-top:12px; }
.ocx .occ-title { font-family:var(--font-display); font-size:20px; font-weight:700; line-height:26px; color:var(--ink); margin:0; overflow-wrap:anywhere; }
.ocx .occ-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.ocx .occ-rename { display:flex; gap:8px; align-items:center; flex-wrap:wrap; flex:1 1 280px; }
.ocx .occ-rename .oc-input { flex:1 1 200px; padding:9px 12px; font-size:15px; }
.ocx .occ-err { color:var(--error-300); font-size:13px; margin-top:8px; }
.ocx .occ-empty { padding:56px 10px; text-align:center; font-family:var(--font-display); font-size:15px; font-weight:500; color:var(--gray-300); }

/* ---------- the "…" menu on a collection ---------- */
.ocx .occ-menu-wrap { position:relative; }
.ocx .occ-menu { position:absolute; right:0; top:calc(100% + 6px); z-index:30; min-width:190px; background:var(--gray-0); border:1px solid var(--border); border-radius:10px; box-shadow:0 12px 28px rgba(16,24,40,.16); padding:6px; display:flex; flex-direction:column; }
.ocx .occ-menu button { text-align:left; font-size:14px; padding:9px 12px; border-radius:7px; color:var(--gray-400); }
.ocx .occ-menu button:hover, .ocx .occ-menu button:focus-visible { background:var(--gray-50); outline:none; }
.ocx .occ-menu button.danger { color:var(--error-300); }

/* ---------- the save picker (CollectionSheet with a card) ---------- */
.ocx .occ-pick { display:flex; align-items:center; gap:12px; width:100%; padding:10px; border-radius:10px; text-align:left; }
.ocx .occ-pick:hover, .ocx .occ-pick:focus-visible { background:var(--gray-50); outline:none; }
.ocx .occ-pick[aria-checked="true"] { background:var(--primary-100); }
.ocx .occ-pick .tick { margin-left:auto; color:var(--gray-200); display:flex; }
.ocx .occ-pick[aria-checked="true"] .tick { color:var(--success-400); }
.ocx .occ-remove { font-size:13px; font-weight:700; color:var(--error-300); }
.ocx .occ-remove:hover { text-decoration:underline; text-underline-offset:3px; }

/* ---------- a shared collection, opened from a link inside OCEAN ---------- */
.ocx .occ-shared { max-width:880px; }
.ocx .occ-shared .oc-sgrid { margin-top:16px; }
.ocx .occ-scard-top { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }

@media (max-width:860px) {
  .ocx .occ-grid { grid-template-columns:minmax(0,1fr); }
  .ocx .occ-title { font-size:18px; line-height:24px; }
}
`;

let injected = false;

export function useCollectionStyles(): void {
  useLayoutEffect(() => {
    if (injected || typeof document === 'undefined') return;
    injected = true;
    const style = document.createElement('style');
    style.setAttribute('data-ocean-collections', '');
    style.textContent = CSS;
    document.head.appendChild(style);
  }, []);
}
